import { AnalyticsCategory, useAnalytics } from 'lib/analytics';
import {
  useAskKlueAppType,
  useAskKlueResearchMode,
  useAskKlueThreadId,
} from 'store/views/dashboard';

import type {
  AskKlueAppType,
  AskKlueContextPageType,
} from 'store/api/ask-klue';

export type AskKlueEventSuggestType = {
  template: string;
  rivalId: number;
  rivalName: string;
  rivalRank: number;
  text: string;
  iconUrl: string;
};

/**
 * Ask Klue event
 * (Generated from https://transform.tools/json-schema-to-typescript)
 */
export type AskKlueEvent = {
  /**
   * Generated hash id for Klue Object
   */
  id: string;
  /**
   * The type of ask klue action
   */
  type:
    | 'ask'
    | 'ask_followup'
    | 'ask_suggest'
    | 'render_response'
    | 'copy_result'
    | 'positive_feedback'
    | 'negative_feedback'
    | 'close_modal'
    | 'open_modal'
    | 'render_ask_klue'
    | 'render_ask_suggest'
    | 'focus_text_input_box'
    | 'yes_ask_klue_feedback'
    | 'no_ask_klue_feedback'
    | 'skip_ask_klue_feedback'
    | 'open_answer_source_item'
    | 'click_chat_session_item'
    | 'create_ask_klue_card'
    | 'view_ask_klue_card';
  /**
   * Question/Search text
   */
  text?: string | null;
  /**
   * result
   */
  result?: string | null;
  /**
   * App type
   */
  appType?: AskKlueAppType;
  /**
   * Session ID
   */
  threadId?: string;
  /**
   * Page - Identify which page the Ask Klue was rendered
   */
  page?: AskKlueContextPageType;
  /**
   * AskKlueSuggest - Entity with Single Prompt suggest data
   */
  askKlueSuggest?: AskKlueEventSuggestType[];
  /**
   * Text Feedback - If the user clicks "No" in the Ask Klue Feedback Dialog
   */
  textFeedback?: string;
  /**
   * Is Ask Klue Feedback Dialog - If the user fills out the Ask Klue Feedback Dialog
   */
  isAskKlueFeedbackDialog?: boolean;
  /**
   * Is Research Mode - If the user selected research mode
   */
  isResearchMode?: boolean;
};

type AskKlueLogType = {
  askKlueId: AskKlueEvent['id'];
  eventType: AskKlueEvent['type'];
  appType?: AskKlueEvent['appType'];
  text?: AskKlueEvent['text'];
  page?: AskKlueEvent['page'];
  askKlueSuggest?: AskKlueEvent['askKlueSuggest'];
  textFeedback?: AskKlueEvent['textFeedback'];
  isAskKlueFeedbackDialog?: AskKlueEvent['isAskKlueFeedbackDialog'];
  isResearchMode?: AskKlueEvent['isResearchMode'];
};
function useAskKlueLog() {
  const { logEvent } = useAnalytics();
  const askKlueAppType = useAskKlueAppType();
  const threadId = useAskKlueThreadId();
  const isResearchMode = useAskKlueResearchMode();
  return ({
    askKlueId: id,
    eventType: action,
    appType,
    text,
    page,
    askKlueSuggest,
    textFeedback,
    isAskKlueFeedbackDialog,
  }: AskKlueLogType) =>
    logEvent({
      event: {
        category: AnalyticsCategory.askKlueInteraction,
        action,
      },
      data: {
        id,
        appType: appType ?? askKlueAppType,
        text,
        threadId,
        page,
        askKlueSuggest,
        textFeedback,
        isAskKlueFeedbackDialog,
        isResearchMode,
      },
    });
}

export default useAskKlueLog;
